import { useCallback, useMemo, useRef, useState } from 'react';
import useDraftState, { type TSetDraft } from '@/creditflowresearch/application/hooks/useDraftState';
import type { IApiColumnConfig, TColumnScope } from '@/creditflowresearch/domain/vo/Bondscreener';

export interface IColumnCustomizationEntry {
  id: string,
  label: string,
  isVisible: boolean,
  isLocked: boolean,
  scope: TColumnScope[],
}

interface IUseColumnsCustomizationProps {
  allColumns: readonly IApiColumnConfig[],
  activeColumnsIds: readonly string[],
  defaultColumnsIds: readonly string[],
  lockedColumnIds: ReadonlySet<string>,
  onApply: (ids: string[]) => void,
}

interface IUseColumnsCustomizationReturn {
  draftColumns: IColumnCustomizationEntry[],
  setDraftColumns: TSetDraft<IColumnCustomizationEntry[]>,
  getDraftColumns: () => IColumnCustomizationEntry[],
  applyColumns: () => void,
  resetColumns: () => void,
  discardColumns: () => void,
  applyExternalColumns: (ids: string[]) => void,
  appliedColumnIds: string[],
  resetKey: number,
}

const toEntry = (column: IApiColumnConfig, isVisible: boolean, isLocked: boolean): IColumnCustomizationEntry => ({
  id: column.id,
  label: column.label,
  isVisible,
  isLocked,
  scope: column.scope,
});

export const getVisibleIds = (columns: IColumnCustomizationEntry[]): string[] =>
  columns.filter((column) => column.isVisible).map((column) => column.id);

const buildColumnEntries = (
  allColumns: readonly IApiColumnConfig[],
  activeColumnsIds: readonly string[],
  lockedColumnIds: ReadonlySet<string>,
): IColumnCustomizationEntry[] => {
  const columnsById = new Map(allColumns.map((column) => [column.id, column]));

  const lockedEntries = allColumns
    .filter((column) => lockedColumnIds.has(column.id))
    .map((column) => toEntry(column, true, true));

  const visibleColumnsInSavedOrder = activeColumnsIds
    .map((id) => columnsById.get(id))
    .filter((column): column is IApiColumnConfig => column !== undefined && !lockedColumnIds.has(column.id));

  const visibleIds = new Set(visibleColumnsInSavedOrder.map((column) => column.id));
  const nextVisibleColumns = [...visibleColumnsInSavedOrder];

  const editableEntries = allColumns
    .filter((column) => !lockedColumnIds.has(column.id))
    .map((column) => (
      visibleIds.has(column.id)
        ? toEntry(nextVisibleColumns.shift() as IApiColumnConfig, true, false)
        : toEntry(column, false, false)
    ));

  return [...lockedEntries, ...editableEntries];
};

const useColumnsCustomization = ({
  allColumns,
  activeColumnsIds,
  defaultColumnsIds,
  lockedColumnIds,
  onApply,
}: IUseColumnsCustomizationProps): IUseColumnsCustomizationReturn => {
  const initialColumns = buildColumnEntries(allColumns, activeColumnsIds, lockedColumnIds);

  const [draftColumns, setDraftColumnsState] = useState<IColumnCustomizationEntry[]>(initialColumns);

  const {
    getDraft: getDraftColumns,
    setDraft: setDraftRef,
    resetTo,
    resetKey,
  } = useDraftState<IColumnCustomizationEntry[]>(initialColumns);

  const appliedColumnsRef = useRef<IColumnCustomizationEntry[]>(initialColumns);

  const setDraftColumns = useCallback<TSetDraft<IColumnCustomizationEntry[]>>((updater) => {
    setDraftColumnsState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setDraftRef(next);
      return next;
    });
  }, [setDraftRef]);

  const replaceDraft = useCallback((next: IColumnCustomizationEntry[]): void => {
    resetTo(next);
    setDraftColumnsState(next);
  }, [resetTo]);

  const applyColumns = useCallback((): void => {
    const current = getDraftColumns();
    appliedColumnsRef.current = current;
    resetTo(current);
    onApply(getVisibleIds(current));
  }, [getDraftColumns, resetTo, onApply]);

  const resetColumns = useCallback((): void => {
    replaceDraft(buildColumnEntries(allColumns, defaultColumnsIds, lockedColumnIds));
  }, [allColumns, defaultColumnsIds, lockedColumnIds, replaceDraft]);

  const discardColumns = useCallback((): void => {
    replaceDraft(appliedColumnsRef.current);
  }, [replaceDraft]);

  const applyExternalColumns = useCallback((ids: string[]): void => {
    replaceDraft(buildColumnEntries(allColumns, ids, lockedColumnIds));
  }, [allColumns, lockedColumnIds, replaceDraft]);

  const appliedColumnIds = useMemo(
    () => getVisibleIds(appliedColumnsRef.current),
    [resetKey],
  );

  return useMemo(() => ({
    draftColumns,
    setDraftColumns,
    getDraftColumns,
    applyColumns,
    resetColumns,
    discardColumns,
    applyExternalColumns,
    appliedColumnIds,
    resetKey,
  }), [
    draftColumns, setDraftColumns, getDraftColumns, applyColumns,
    resetColumns, discardColumns, applyExternalColumns, appliedColumnIds, resetKey,
  ]);
};

export default useColumnsCustomization;
